import React, { useState } from 'react';
import './StepPanel.css';
import api from '../wrapper/API';
import Popup from '../popup/Popup';
import ActionButton from '../input/ActionButton';
import Icon from '../home/Icon';
import { usePopup } from '../context/PopupContext';
import { useSuccessBar } from '../context/SuccessBarContext';
import AssignLeadersPanel from './AssignLeadersPanel';
import ReleasePanel from './ReleasePanel';
import SendMailPanel from './SendMailPanel';
import ManualCheckPanel from './ManualCheckPanel';
import {
  apiErrorMessage, eventNouns, isFinalStep, normalizeStepKind, STEP_KIND, STEP_KIND_LABEL, stepIcon,
  targetsSummary,
} from './processUtils';

const RESET_POPUP = 'processresetstep';

// What stays when a step is set back to "Nicht erledigt" (only the status changes).
const RESET_HINT = {
  AssignLeaders: 'Eingetragene Spieltagleiter bleiben eingetragen.',
  Release: 'Gesetzte Freigaben bleiben bestehen – zum Entfernen „Freigabe zurücknehmen" nutzen.',
  SendMail: 'Versendete Mails bleiben versendet. Wird der Schritt danach erneut ausgeführt, geht die Mail erneut an alle Empfänger.',
};

// Synthetic final step: waiting for completion (the process closes itself once all events are
// completed or cancelled).
function FinalStepPanel({ process, onNavigateStep }) {
  const nouns = eventNouns(process);
  const openSteps = (process.steps ?? []).filter(s => !isFinalStep(s) && s.status === 'pending');
  return (
    <div className={'processActionPanel isDone'}>
      <div className={'processActionHeader'}>
        {openSteps.length === 0
          ? <span className={'processActionBadge isDone'}>Alle Schritte erledigt</span>
          : <span className={'processActionBadge'}>{openSteps.length} offen</span>}
        <h3>Auf Abschluss warten</h3>
      </div>
      <p className={'processActionDesc'}>
        {openSteps.length === 0
          ? `Alle Schritte sind erledigt oder übersprungen. Der Prozess wird automatisch abgeschlossen und schreibgeschützt, sobald alle ${nouns.many} abgeschlossen oder abgesagt sind.`
          : `Der Prozess wird automatisch abgeschlossen, sobald alle ${nouns.many} abgeschlossen oder abgesagt sind. Noch offene Schritte:`}
      </p>
      {openSteps.length > 0 && (
        <div className={'processActionButtons'}>
          {openSteps.map(s => (
            <ActionButton key={s.key} icon={stepIcon(s)} label={s.label} onClick={() => onNavigateStep(s.key)} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Panel of the viewed process step: header (status badge, label, kind · targets), the kind-specific
 * body (AssignLeaders / Release / SendMail / ManualCheck) and the step menu (Überspringen, Abbrechen,
 * Auf „Nicht erledigt" setzen). Keyed by step key in the page, so local state (re-execute mode, mail
 * drafts) resets when another step is viewed.
 */
function StepPanel({
  process, step, isCurrent, groupsById, leaderCandidates, ensureLeaderCandidates,
  onProcess, onReload, reportError, onNavigateStep,
}) {
  const { openPopup } = usePopup();
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();
  // null = status view; 'run' = (re-)execute; 'Resend' / 'CatchUp' = mail modes of a done mail step.
  const [mode, setMode] = useState(null);
  const [busy, setBusy] = useState(false);

  if (isFinalStep(step)) return <FinalStepPanel process={process} onNavigateStep={onNavigateStep} />;

  const kind = normalizeStepKind(step.kind);
  const pending = step.status === 'pending';
  const skipped = step.status === 'skipped';
  const done = step.status === 'done';
  const active = pending || mode != null;
  const execMode = done ? (mode === 'CatchUp' ? 'CatchUp' : 'Resend') : 'Initial';

  const setStatus = (status) => {
    if (busy) return;
    setBusy(true);
    startSuccessBarLoading(status === 'Skipped' ? 'Schritt wird übersprungen' : 'Schritt wird zurückgesetzt');
    api.put(`/EventProcess/${process.id}/Steps/${step.stepId}/Status`, { status })
      .then(response => {
        startSuccessBarSuccess(status === 'Skipped' ? 'Schritt übersprungen' : 'Schritt auf „Nicht erledigt" gesetzt');
        setMode(null);
        // Skip → on to the next open step; reset → stay on this step.
        onProcess(response.data, { resetView: status === 'Skipped' });
      })
      .catch(err => {
        closeSuccessBar();
        reportError(apiErrorMessage(err, status === 'Skipped' ? 'Schritt konnte nicht übersprungen werden.' : 'Schritt konnte nicht zurückgesetzt werden.'));
      })
      .finally(() => setBusy(false));
  };

  // After an execution: back to the status view; jump to the next step unless there are warnings to read.
  const onFinished = (detail, warnings) => {
    setMode(null);
    onProcess(detail, { resetView: !(warnings?.length > 0) });
  };

  const activeActions = pending ? (
    <ActionButton icon={'skip_next'} label={'Überspringen'} type={1} onClick={() => setStatus('Skipped')} disabled={busy} />
  ) : (
    <ActionButton icon={'close'} label={'Abbrechen'} onClick={() => setMode(null)} />
  );
  const resetAction = (
    <ActionButton icon={'undo'} label={'Auf „Nicht erledigt" setzen'} type={1} onClick={() => openPopup(RESET_POPUP, step.stepId)} disabled={busy} />
  );

  let badge;
  if (pending) badge = { text: isCurrent ? 'Nächster Schritt' : 'Offen' };
  else if (mode === 'CatchUp') badge = { text: 'Nachtrag', icon: 'forward_to_inbox' };
  else if (mode === 'Resend') badge = { text: 'Erneut senden', icon: 'replay' };
  else if (mode) badge = { text: skipped ? 'Jetzt ausführen' : 'Erneut ausführen', icon: 'replay' };
  else if (skipped) badge = { text: 'Übersprungen', icon: 'skip_next', cls: 'isSkipped' };
  else badge = { text: 'Erledigt', icon: 'check_circle', cls: 'isDone' };

  const targets = targetsSummary(step, groupsById);
  const subtitle = [STEP_KIND_LABEL[kind], targets].filter(Boolean).join(' · ');
  const panelCls = active ? '' : (skipped ? 'isSkipped' : 'isDone');

  const common = {
    process, step, active, activeActions, resetAction, onStartMode: setMode, onFinished, onProcess, onReload,
    groupsById, reportError, onNavigateStep,
  };

  let body;
  switch (kind) {
    case STEP_KIND.AssignLeaders:
      body = <AssignLeadersPanel {...common} leaderCandidates={leaderCandidates} ensureLeaderCandidates={ensureLeaderCandidates} />;
      break;
    case STEP_KIND.Release:
      body = <ReleasePanel {...common} />;
      break;
    case STEP_KIND.SendMail:
      body = <SendMailPanel {...common} execMode={execMode} />;
      break;
    default:
      body = <ManualCheckPanel {...common} />;
  }

  return (
    <div className={`processActionPanel ${panelCls}`}>
      <div className={'processActionHeader'}>
        <span className={`processActionBadge ${badge.cls ?? ''}`}>
          {badge.icon && <Icon icon={badge.icon} className={'processActionBadgeIcon'} />}
          {badge.text}
        </span>
        <div className={'processActionTitle'}>
          <h3>{step.label}</h3>
          {subtitle && <span className={'processActionSubtitle'}>{subtitle}</span>}
        </div>
      </div>

      {body}

      <Popup
        name={RESET_POPUP}
        confirmText={'Auf „Nicht erledigt" setzen'}
        headerText={'Schritt zurücksetzen'}
        type={'confirmaction'}
        onConfirm={() => setStatus('Pending')}
        withCancel
      >
        <p>Der Schritt <b>„{step.label}"</b> wird wieder auf <b>„Nicht erledigt"</b> gesetzt und gilt damit als offen.</p>
        <p>Bereits ausgeführte Aktionen werden <b>nicht</b> rückgängig gemacht.{RESET_HINT[kind] ? ` ${RESET_HINT[kind]}` : ''}</p>
      </Popup>
    </div>
  );
}

export default StepPanel;
